import React from 'react';
import { useNavigate } from 'react-router-dom';
import CollapsibleSection from './CollapsibleSection';

// Icons
const DollarIcon = () => (
  <svg className="section-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

export interface ArrivedShipment {
  id: string;
  reference: string;
  contractRef?: string;
  supplier: string;
  buyer?: string;
  pod?: string;
  arrivalDate?: string;
  arrivedDate?: string; // legacy
  containerCount?: number;
  revenueType?: string;
  commissionAmount?: number | null;
  commissionRate?: number | null;
  daysSinceArrival?: number | null;
  status?: string;
}

interface ArrivedWithoutInvoiceSectionProps {
  shipments: ArrivedShipment[];
  onCreateInvoice?: (shipment: ArrivedShipment) => void;
}

const ArrivedWithoutInvoiceSection: React.FC<ArrivedWithoutInvoiceSectionProps> = ({
  shipments,
  onCreateInvoice,
}) => {
  const navigate = useNavigate();

  const handleReferenceClick = (reference: string) => {
    navigate(`/contracts/${reference}`);
  };

  if (shipments.length === 0) {
    return (
      <CollapsibleSection
        title="Arrived Without Commission Invoice"
        count={0}
        icon={<DollarIcon />}
      >
        <div className="empty-state">
          <div className="empty-state-icon">✓</div>
          <div className="empty-state-text">All arrived shipments have commission invoices</div>
        </div>
      </CollapsibleSection>
    );
  }

  return (
    <CollapsibleSection
      title="Arrived Without Commission Invoice"
      count={shipments.length}
      icon={<DollarIcon />}
    >
      <div className="table-wrapper">
        <table className="dash-table">
          <thead>
            <tr>
              <th>Reference</th>
              <th>Supplier</th>
              <th>Arrived</th>
              <th>Days Since</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {shipments.map((shipment) => {
              const daysSince = shipment.daysSinceArrival ?? 0;
              const arrivedDateDisplay = shipment.arrivalDate || shipment.arrivedDate || '';
              
              return (
                <tr 
                  key={shipment.id}
                  className={daysSince > 14 ? 'priority-urgent' : 'priority-normal'}
                >
                  <td>
                    <span 
                      className="ref-link"
                      onClick={() => handleReferenceClick(shipment.reference)}
                    >
                      {shipment.reference}
                    </span>
                  </td>
                  <td>{shipment.supplier}</td>
                  <td>{arrivedDateDisplay}</td>
                  <td>
                    <span className={`time-remaining ${daysSince > 14 ? 'overdue' : 'soon'}`}>
                      {daysSince} days
                    </span>
                  </td>
                  <td>
                    <button 
                      className="dash-btn dash-btn-primary dash-btn-sm"
                      onClick={() => onCreateInvoice?.(shipment)}
                    >
                      Create Invoice
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Section Footer */}
      <div className="section-footer">
        <span>{shipments.length} shipment{shipments.length !== 1 ? 's' : ''} awaiting commission invoices</span>
      </div>
    </CollapsibleSection>
  );
};

export default ArrivedWithoutInvoiceSection;
